import { suppressError } from '../promiseUtils';

/*
 * Convert an unknown error object to an error with the default globals context
 */
export function convertToError(errorObject: unknown) {
  return doConvertToError(errorObject, new globalThis.Map<object, Error>());
}

/*
 * Convert an unknown error object to an error
 * When we receive error objects of various origins, sometimes they aren't errors and thus don't
 * include a stack trace. In that case, we create a new error and only take the message from the
 * object.
 */
function doConvertToError(errorObject: unknown, seen: Map<object, Error>): Error {
  let error: Error;
  if (typeof errorObject !== 'object' || errorObject === null) {
    return new Error('Non-error captured');
  }
  // This can be the case if error A has B as its cause, but B has A as its cause.
  if (seen.has(errorObject)) {
    return new Error('Circular error reference');
  }
  // Errors from the same realm can be used directly.
  if (errorObject instanceof Error) {
    error = errorObject;
  } else if (
    isError(errorObject) &&
    // Web extension errors sometimes have an empty string stack, so we explicitly check for
    // undefined here.
    errorObject.stack !== undefined
  ) {
    // This is most likely an error from another realm. We therefore assign it to an error in this realm.
    error = convertToErrorFromErrorConstructor(errorObject);
  } else {
    const keys = Object.keys(errorObject).join(', ');
    if (hasMessage(errorObject)) {
      const { message } = errorObject;
      error = new Error(`Non-error object captured with message "${message}" and keys: ${keys}`);
    } else {
      error = new Error(`Non-error object captured with keys: ${keys}`);
    }
  }

  // To prevent circular causes, we track seen errors.
  seen.set(errorObject, error);

  // We recursively convert cause errors to errors. We do this to ensure that
  // nested cause errors are an instance of errorConstructor.
  if (isError(error.cause)) {
    error.cause = doConvertToError(error.cause, seen);
  }
  return error;
}

/*
 * Create an error with a specific error constructor from an error with a
 * different error constructor. Errors from custom getters that throw on the
 * errorObject are suppressed. This function does therefore NOT ensure that in
 * this case, the custom attributes on the errorObject are copied over.
 */
export function convertToErrorFromErrorConstructor(errorObject: Error): Error {
  let result = new Error(errorObject.message);

  // We want to preserve custom values from the errorObject when converting.
  // Getters can throw errors that should not crash the error tracking. We
  // suppress such errors. This is the reason we can not guarantee the custom
  // values of the errorObject in the return type of this function.
  // Reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign#exceptions
  try {
    result = Object.assign(result, errorObject);
  } catch (assignError) {
    suppressError(assignError);
  }

  result.name = errorObject.name;
  // We only copy these values when they are present in the original object.
  // Not checking these would lead to set properties with the value `undefined`.
  if ('cause' in errorObject) {
    result.cause = errorObject.cause;
  }
  if ('stack' in errorObject) {
    result.stack = errorObject.stack;
  }

  return result;
}

function hasMessage(obj: unknown): obj is { message: string } {
  return (
    typeof obj === 'object' && obj !== null && 'message' in obj && typeof obj.message === 'string'
  );
}

function isError(errorObject: unknown): errorObject is Error {
  return (
    typeof errorObject === 'object' &&
    errorObject != null &&
    'name' in errorObject &&
    typeof errorObject.name === 'string' &&
    'message' in errorObject &&
    typeof errorObject.message === 'string'
  );
}
